import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Check, RefreshCw, AlertCircle } from 'lucide-react';
import { Spinner, Button } from '@tendedloop/ui';
import { TabSwitcher } from '@/components/ui/TabSwitcher';
import { PriorityBadge } from '@/components/ui/PriorityBadge';
import { AmenityIcon } from '@/components/ui/AmenityIcon';
import { EmptyState } from '@/components/ui/EmptyState';
import { useTaskStore } from '@/stores/taskStore';

// Map priority from API to display format
function mapPriority(priority: string): 'P0' | 'P1' | 'P2' {
  switch (priority) {
    case 'critical': return 'P0';
    case 'high': return 'P1';
    case 'medium': return 'P2';
    case 'low': return 'P2'; // Map low to P2 (no P3 in PriorityBadge)
    default: return 'P2';
  }
}

export function TaskList() {
  const navigate = useNavigate();
  const { t } = useTranslation('operator');
  const [activeTab, setActiveTab] = useState('my-tasks');

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return t('time.justNow');
    if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
    if (diffMins < 1440) return t('time.hoursAgo', { count: Math.floor(diffMins / 60) });
    return t('time.daysAgo', { count: Math.floor(diffMins / 1440) });
  };

  const {
    tasks,
    isLoading,
    error,
    fetchTasks,
    clearError,
  } = useTaskStore();

  // Fetch tasks on mount
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const pendingTasks = tasks.filter(t => t.status !== 'COMPLETED');
  const completedTasks = tasks.filter(t => t.status === 'COMPLETED');

  const tabs = [
    { id: 'my-tasks', label: t('tasks.myTasks'), count: pendingTasks.length },
    { id: 'all-tasks', label: t('tasks.allTasks') },
  ];

  const handleRefresh = () => {
    clearError();
    fetchTasks();
  };

  if (isLoading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] px-6 text-center">
        <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="h-8 w-8 text-error" />
        </div>
        <h2 className="text-xl font-semibold text-neutral-dark mb-2">
          {t('tasks.failedToLoad')}
        </h2>
        <p className="text-neutral-medium mb-6">{error}</p>
        <Button
          variant="outline"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={handleRefresh}
        >
          {t('common:buttons.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <TabSwitcher tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <div className="px-4 py-6">
        {/* Pull to refresh indicator */}
        {isLoading && tasks.length > 0 && (
          <div className="flex justify-center mb-4">
            <Spinner size="sm" />
          </div>
        )}

        {pendingTasks.length === 0 && completedTasks.length === 0 ? (
          <EmptyState
            icon={<Check className="h-8 w-8" />}
            title={t('tasks.noPendingTasks')}
            description={t('tasks.noTasksDescription')}
          />
        ) : (
          <>
            {/* Pending Tasks */}
            {pendingTasks.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xs font-semibold text-neutral-medium tracking-wider mb-3">
                  {t('tasks.todayLabel')} · {pendingTasks.length} {t('tasks.taskCount', { count: pendingTasks.length })}
                </h2>
                <div className="space-y-3">
                  {pendingTasks.map(task => (
                    <button
                      key={task.id}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="w-full bg-white border border-neutral-light rounded-xl p-4 text-start hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <AmenityIcon
                            type={task.amenity.amenityType.icon || task.amenity.amenityType.name.toLowerCase()}
                            size="md"
                          />
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <PriorityBadge priority={mapPriority(task.priority)} />
                              <span className="font-medium text-neutral-dark">
                                {task.amenity.name}
                              </span>
                            </div>
                            <p className="text-sm text-neutral-medium">
                              {task.feedback.issues.map(i => i.name).join(', ')}
                            </p>
                            <p className="text-xs text-neutral-medium mt-1">
                              {task.amenity.building.name} · {formatRelativeTime(task.createdAt)}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-neutral-medium" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Tasks */}
            {completedTasks.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-neutral-medium tracking-wider mb-3">
                  {t('tasks.completedLabel')} · {completedTasks.length} {t('tasks.taskCount', { count: completedTasks.length })}
                </h2>
                <div className="space-y-2">
                  {completedTasks.map(task => (
                    <div
                      key={task.id}
                      className="bg-white border border-neutral-light rounded-xl p-4 opacity-70"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <span className="font-medium text-neutral-dark">
                            {task.amenity.name}
                          </span>
                          <p className="text-xs text-neutral-medium">
                            {t('tasks.completedTime', { time: task.completedAt ? formatRelativeTime(task.completedAt) : t('time.justNow') })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
